import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import * as React from 'react';
import { useRef } from 'react';
import { ImageBackground, Platform } from 'react-native';

import { BgLandscapePng, BgPortraitPng } from '../assets';
import { firebaseAnalytics } from '../services/firebase/firebase.service';
import { preloadForRoute } from '../services/preloadAssets';
import { navigationRef } from '../utils/helpers/navigationService';
import { formatSvaraWebDocumentTitle } from '../utils/helpers/svaraWebDocumentTitle';
import { useDevice } from '../utils/hooks/useDevice';
import BannedModal from './Components/Modals/Login/BannedModal';
import type { MessageCenterTab } from './Components/Modals/MessagesModal';
import PaymentCallbackScreen from './Components/PaymentCallback/EPayCallback';
import AchievementsHost from './navigation/AchievementsHost';
import ActiveTableRestoreHost from './navigation/ActiveTableRestoreHost';
import AntiChurnBonusListener from './navigation/AntiChurnBonusListener';
import DailyMissionsHost from './navigation/DailyMissionsHost';
import DuelsHost from './navigation/DuelsHost';
import GlobalGameBroadcastListener from './navigation/GlobalGameBroadcastListener';
import NotificationsListener from './navigation/NotificationsListener';
import PrivateChatListener from './navigation/PrivateChatListener';
import { notifyTransitionEnd } from './navigation/transitionEvents';
import VipTicketBalanceHost from './navigation/VipTicketBalanceHost';
import AchievementsScreen from './screens/AchievementsScreen';
import BlockListScreen from './screens/BlockListScreen';
import BonusesScreen from './screens/BonusesScreen';
import BuyPackagesScreen from './screens/BuyPackagesScreen';
import ConversationsScreen from './screens/ConversationsScreen';
import DuelHistoryScreen from './screens/DuelHistoryScreen';
import DuelRedemptionHistoryScreen from './screens/DuelRedemptionHistoryScreen';
import DuelResultScreen from './screens/DuelResultScreen';
import DuelRewardScreen from './screens/DuelRewardScreen';
import DuelScoreBreakdownScreen from './screens/DuelScoreBreakdownScreen';
import DuelSeasonScreen from './screens/DuelSeasonScreen';
import DuelsScreen from './screens/DuelsScreen';
import EventScreen from './screens/EventScreen';
import FriendsScreen from './screens/FriendsScreen';
import GameRulesScreen from './screens/GameRulesScreen';
import GameScreen from './screens/GameScreen';
import HelpScreen from './screens/HelpScreen';
import InventoryScreen from './screens/InventoryScreen';
import InvitesScreen from './screens/InvitesScreen';
import JoinTournamentsScreen from './screens/JoinTournamentScreen';
import LoginScreen from './screens/LoginScreen';
import {
	ActiveTournamentScreen,
	LobbyScreen,
	MainScreen,
	RoomScreen,
	TournamentScreen,
	TournamentsScreen,
} from './screens/MainScreens';
import MessagesScreen from './screens/MessagesScreen';
import MyTablesScreen from './screens/MyTablesScreen';
import PaymentErrorScreen from './screens/PaymentErrorScreen';
import PaymentSuccessScreen from './screens/PaymentSuccessScreen';
import PersonalDataScreen from './screens/PersonalDataScreen';
import PrivateChatScreen from './screens/PrivateChatScreen';
import ProfileScreen from './screens/ProfileScreen';
import RankingScreen from './screens/RankingScreen';
import SearchScreen from './screens/SearchScreen';
import SettingsScreen from './screens/SettingsScreen';
import TutorialScreen from './screens/TutorialScreen';
import VipActiveMemberScreen from './screens/VipActiveMemberScreen';
import VipRoomsScreen from './screens/VipRoomsScreen';
import VipSubscriptionScreen from './screens/VipSubscriptionScreen';
import VipTablesScreen from './screens/VipTablesScreen';
export type RootStackParamList = {
	Main: undefined;
	Ranking: { category?: 'duels' } | undefined;
	Lobby:
		| {
				lobbyPage?: number;
		  }
		| undefined;
	Login: undefined;
	Settings: undefined;
	Invites: undefined;
	/** The Message Center, on the Notifications tab unless `tab` says otherwise. */
	Messages: { tab?: MessageCenterTab } | undefined;
	/** The private chat with `playerId`, unlocked by a donation between the two players. */
	PrivateChat: { playerId: string; username?: string };
	/** The `chats` link: opens the Message Center on its Private Chat tab. */
	Conversations: undefined;
	/** The players you blocked, from your profile. */
	BlockList: undefined;
	Profile: { edit?: boolean } | undefined;
	GameRules: undefined;
	MyTables: undefined;
	VipTables: undefined;
	VipRooms: undefined;
	Search: undefined;
	PersonalData: undefined;
	Help: undefined;
	JoinTournaments:
		| {
				tournamentId: string;
				entryLevel?: number;
		  }
		| undefined;
	BuyPackages: {
		activeTab?: 'chips' | 'vip' | 'tournaments';
		playerId?: string;
		/** Set, the store is the DONATION STORE: whatever is bought is credited to this player. */
		recipient?: { playerId: string; username: string };
	};
	VipActiveMember: undefined;
	VipSubscription: undefined;
	Bonuses: undefined;
	Inventory:
		| {
				tab?: 'inventory' | 'friends' | 'profile';
				category?: 'frame' | 'background' | 'badge' | 'featured' | 'rewards';
				edit?: boolean;
		  }
		| undefined;
	Achievements: undefined;
	Duels: undefined;
	DuelResult: { duelId: string };
	DuelScoreBreakdown: { duelId: string };
	DuelHistory: undefined;
	DuelSeason: undefined;
	DuelReward: { entitlementId: string };
	DuelRedemptionHistory: undefined;
	Event: undefined;
	Friends: undefined;
	Tournaments: undefined;
	Rooms: undefined;
	Game: {
		tableId?: number | string;
		legacyRoomId?: number;
		roomSlug?: string;
		isTutorial?: boolean;
		tableName?: string;
		tableStringId?: string;
		isTournamentRoom?: boolean;
		timestamp?: number;
	};
	Tutorial: {
		tableId?: number | string;
		legacyRoomId?: number;
		roomSlug?: string;
		isTutorial?: boolean;
		tableName?: string;
		tableStringId?: string;
		isTournamentRoom?: boolean;
		timestamp?: number;
	};
	LobbyTables: undefined;
	Room: { roomSlug: string; roomId: string };
	Tournament: { tournamentId: string; roomId: string };
	PaymentCallback: undefined;
	PaymentSuccess: undefined;
	PaymentError: undefined;
	ActiveTournament: { roomId: string; roomName?: string };
};

const Stack = createNativeStackNavigator<RootStackParamList>();

const sideMenuModalOptions = {
	presentation: Platform.OS === 'ios' ? 'containedTransparentModal' : 'transparentModal',
	animation: 'fade',
	gestureEnabled: false,
	contentStyle: { backgroundColor: 'transparent' },
} as const;

const linking = {
	prefixes: [
		// Native deep link scheme; web origin not needed here
		'svara://',
		'/',
	],
	config: {
		screens: {
			// svara://invite/<code> lands on home; the code itself is picked up by useInviteLinkCapture.
			Main: { path: '/', alias: ['invite/:code'] },
			Lobby: 'lobby',
			Tournaments: 'tournaments',
			Tournament: 'tournament/:tournamentId/room/:roomId',
			ActiveTournament: 'active-tournament/:roomId',
			Room: 'room/:roomSlug',
			Ranking: 'ranking',
			Login: 'login',
			Settings: 'settings',
			Invites: 'invites',
			Messages: 'messages',
			Profile: 'profile',
			GameRules: 'rules',
			Search: 'search',
			PersonalData: 'personal-data',
			VipSubscription: 'vip-subscription',
			VipActiveMember: 'vip-active-member',
			Rooms: 'rooms',
			Game: 'game',
			LobbyTables: 'lobby-tables',
			Duels: 'duels',
			Bonuses: 'bonuses',
			Inventory: 'inventory',
			Achievements: 'achievements',
			Event: 'event',
			Friends: 'friends',
			// The notification and push links are `/chat/<playerId>`.
			PrivateChat: 'chat/:playerId',
			Conversations: 'chats',
		},
	},
};

// Only use URL linking on native so web doesn't change URL and trigger reloads/404s
// (linking prop is set inside RootNavigator via useDevice().isWeb)

export default function RootNavigator() {
	const routeNameRef = useRef<string | undefined>(undefined);
	const { isWeb, isDesktop } = useDevice();
	const linkingEnabled = !isWeb ? linking : undefined;

	// The app's one and only backdrop. It lives here rather than in MainLayout so it is painted
	// exactly once: MainLayout used to paint its own copy on top, which meant two full-screen
	// images stacked - and two different ones as soon as the orientation assets diverged.
	const backgroundSource = isDesktop ? BgLandscapePng : BgPortraitPng;

	return (
		<NavigationContainer
			linking={linkingEnabled}
			ref={navigationRef}
			onReady={() => {
				const name = navigationRef.current?.getCurrentRoute()?.name;
				routeNameRef.current = name;
				preloadForRoute(name);
				if (isWeb && typeof document !== 'undefined' && name) {
					document.title = formatSvaraWebDocumentTitle(name);
				}
				console.log('🧭 [NAV] ready');
			}}
			onStateChange={async () => {
				const previousRouteName = routeNameRef.current;
				const currentRouteName = navigationRef.current?.getCurrentRoute()?.name;

				if (previousRouteName !== currentRouteName && currentRouteName) {
					// 🔥 Track screen view on navigation
					if (firebaseAnalytics && typeof firebaseAnalytics.logScreenView === 'function') {
						try {
							await firebaseAnalytics.logScreenView(currentRouteName);
						} catch (error) {
							console.warn('[Navigator] Failed to log screen view:', error);
						}
					}
				}

				// Save the current route name for next time
				routeNameRef.current = currentRouteName;
				preloadForRoute(currentRouteName);

				if (isWeb && typeof document !== 'undefined' && currentRouteName) {
					document.title = formatSvaraWebDocumentTitle(currentRouteName);
				}
			}}
		>
			<NotificationsListener />
			<PrivateChatListener />
			<GlobalGameBroadcastListener />
			<AntiChurnBonusListener />
			<DailyMissionsHost />
			<AchievementsHost />
			<DuelsHost />
			<VipTicketBalanceHost />
			<ActiveTableRestoreHost />
			<ImageBackground source={backgroundSource} style={styles.background} resizeMode='cover'>
				<Stack.Navigator
					screenListeners={{ transitionEnd: notifyTransitionEnd }}
					screenOptions={({ route }) => ({
						headerShown: false,
						contentStyle: { backgroundColor: 'transparent' },
						// Freeze fully-covered background screens so they stop re-rendering
						// (each mounted screen carries its own ticking AppFooter). Screens under
						// transparent modals keep activityState > 0 and are never frozen, so
						// visible backdrops keep updating. Game/Tutorial are exempt: their live
						// game WebView must never be suspended.
						freezeOnBlur: route.name !== 'Game' && route.name !== 'Tutorial',
						...(isWeb ? { title: formatSvaraWebDocumentTitle(route.name) } : {}),
					})}
				>
					<Stack.Screen name='Main' component={MainScreen} options={{ animation: 'fade' }} />
					<Stack.Screen name='Lobby' component={LobbyScreen} options={{ animation: 'fade' }} />
					<Stack.Screen
						name='Tournaments'
						component={TournamentsScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='Tournament'
						component={TournamentScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='Room'
						component={RoomScreen}
						initialParams={{ roomSlug: 'sofia' }}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='ActiveTournament'
						component={ActiveTournamentScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='Game'
						component={GameScreen}
						options={{
							headerShown: false,
							presentation: 'fullScreenModal',
							gestureEnabled: false,
						}}
					/>

					<Stack.Screen
						name='Tutorial'
						component={TutorialScreen}
						options={{
							headerShown: false,
							presentation: 'fullScreenModal',
							gestureEnabled: true, // Enable swipe back gesture
						}}
					/>

					<Stack.Screen
						name='Ranking'
						component={RankingScreen}
						options={{
							animation: 'fade',
						}}
					/>
					<Stack.Screen
						name='VipTables'
						component={VipTablesScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen
						name='VipRooms'
						component={VipRoomsScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='JoinTournaments'
						component={JoinTournamentsScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen name='Login' component={LoginScreen} options={sideMenuModalOptions} />
					<Stack.Screen name='Settings' component={SettingsScreen} options={sideMenuModalOptions} />
					<Stack.Screen name='Invites' component={InvitesScreen} options={sideMenuModalOptions} />
					<Stack.Screen
						name='Messages'
						component={MessagesScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					{/* The chat list and a chat open over the inbox the way the inbox opens over the app. */}
					<Stack.Screen
						name='Conversations'
						component={ConversationsScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen
						name='BlockList'
						component={BlockListScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen
						name='PrivateChat'
						component={PrivateChatScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen name='Help' component={HelpScreen} options={sideMenuModalOptions} />
					<Stack.Screen
						name='VipSubscription'
						component={VipSubscriptionScreen}
						options={{ animation: 'fade', presentation: 'card' }}
					/>
					<Stack.Screen
						name='VipActiveMember'
						component={VipActiveMemberScreen}
						options={{ animation: 'fade', presentation: 'card' }}
					/>
					{/* Profile is one of this modal's three tabs, alongside Inventory and Achievements -
					    it has to share their `sideMenuModalOptions` (a `containedTransparentModal` on
					    iOS) rather than a plain `transparentModal`, or switching to/from it presents a
					    real separate native modal instead of swapping the contained overlay in place. */}
					<Stack.Screen name='Profile' component={ProfileScreen} options={sideMenuModalOptions} />
					<Stack.Screen
						name='BuyPackages'
						component={BuyPackagesScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen
						name='GameRules'
						component={GameRulesScreen}
						options={sideMenuModalOptions}
					/>
					<Stack.Screen
						name='MyTables'
						component={MyTablesScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen
						name='Search'
						component={SearchScreen}
						options={{
							presentation: 'transparentModal',
						}}
					/>
					<Stack.Screen
						name='PersonalData'
						component={PersonalDataScreen}
						options={sideMenuModalOptions}
					/>

					<Stack.Screen
						name='PaymentCallback'
						component={PaymentCallbackScreen}
						options={{
							headerShown: false,
							presentation: 'fullScreenModal',
						}}
					/>
					<Stack.Screen
						name='PaymentSuccess'
						component={PaymentSuccessScreen}
						options={{
							headerShown: false,
							presentation: 'fullScreenModal',
						}}
					/>
					<Stack.Screen
						name='PaymentError'
						component={PaymentErrorScreen}
						options={{
							headerShown: false,
							presentation: 'fullScreenModal',
						}}
					/>
					<Stack.Screen name='Duels' component={DuelsScreen} options={{ animation: 'fade' }} />
					<Stack.Screen
						name='DuelResult'
						component={DuelResultScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='DuelScoreBreakdown'
						component={DuelScoreBreakdownScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='DuelHistory'
						component={DuelHistoryScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='DuelSeason'
						component={DuelSeasonScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='DuelReward'
						component={DuelRewardScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen
						name='DuelRedemptionHistory'
						component={DuelRedemptionHistoryScreen}
						options={{ animation: 'fade' }}
					/>
					<Stack.Screen name='Event' component={EventScreen} options={{ animation: 'fade' }} />
					<Stack.Screen name='Bonuses' component={BonusesScreen} options={{ animation: 'fade' }} />
					{/* The design draws the inventory as a scrim over whatever is behind it, so it is
					    presented the way the side menu itself is rather than as an opaque screen. */}
					<Stack.Screen
						name='Inventory'
						component={InventoryScreen}
						options={sideMenuModalOptions}
					/>
					{/* The achievements modal is drawn the same way - a panel over a scrim. */}
					<Stack.Screen
						name='Achievements'
						component={AchievementsScreen}
						options={sideMenuModalOptions}
					/>
					<Stack.Screen name='Friends' component={FriendsScreen} options={{ animation: 'fade' }} />
				</Stack.Navigator>
				<BannedModal />
			</ImageBackground>
		</NavigationContainer>
	);
}

const styles = {
	background: { flex: 1 },
};
